(() => {
  if (customElements.get('calinfi-email-popup')) return;

  const STORAGE_KEYS = {
    shown: 'calinfiEmailPopupShown:v1',
    closedUntil: 'calinfiEmailPopupClosedUntil:v1',
    subscribed: 'calinfiEmailPopupSubscribed:v1',
    pending: 'calinfiEmailPopupPending:v1',
    leadPending: 'calinfiEmailPopupLeadPending:v1',
    leadFired: 'calinfiEmailPopupLeadFired:v1',
  };

  const DAY_IN_MS = 24 * 60 * 60 * 1000;
  const MOBILE_QUERY = '(max-width: 749px)';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function readStorage(storage, key) {
    try {
      return storage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  function writeStorage(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch (_error) {
      // Storage can be unavailable in privacy-restricted browsers. The popup still works for this page view.
    }
  }

  function isVisible(element) {
    if (!(element instanceof HTMLElement)) return false;
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    return element.getClientRects().length > 0;
  }

  class CalinfiEmailPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.dialog = this.querySelector('[data-calinfi-email-popup-dialog]');
      this.closeButton = this.querySelector('[data-calinfi-email-popup-close]');
      this.form = this.querySelector('form');
      this.tagsInput = this.form?.querySelector('input[name="contact[tags]"]');
      this.returnToInput = this.form?.querySelector('input[name="return_to"]');
      this.formPanel = this.querySelector('[data-calinfi-email-popup-form-panel]');
      this.successPanel = this.querySelector('[data-calinfi-email-popup-success-panel]');
      this.serverState = this.querySelector('[data-calinfi-email-popup-server-state]')?.dataset
        .calinfiEmailPopupServerState;
      this.copyButton = this.querySelector('[data-calinfi-email-popup-copy]');
      this.copyLabel = this.querySelector('[data-calinfi-email-popup-copy-label]');
      this.copyAriaLabel = this.copyButton?.getAttribute('aria-label') || '';
      this.discountLink = this.querySelector('[data-calinfi-email-popup-discount-link]');
      this.discountCode = this.dataset.discountCode || 'WELCOME10';
      this.previousFocus = null;
      this.isSuccess = false;
      this.isCampaignVisit = false;
      this.retryTimer = null;
      this.openTimer = null;
      this.copyResetTimer = null;

      if (!this.dialog) return;

      this.handleCloseClick = () => this.closePopup('dismiss');
      this.handleDialogClick = (event) => {
        if (event.target === this.dialog) this.closePopup('dismiss');
      };
      this.handleCancel = (event) => {
        event.preventDefault();
        this.closePopup('dismiss');
      };
      this.handleDialogClose = () => this.afterClose();
      this.handleKeydown = (event) => this.trapFocus(event);
      this.handleExitIntent = (event) => {
        if (event.relatedTarget || event.clientY > 0) return;
        this.requestOpen('exit-intent');
      };
      this.handleFormSubmit = () => {
        writeStorage(window.sessionStorage, STORAGE_KEYS.pending, 'true');
        writeStorage(window.sessionStorage, STORAGE_KEYS.leadPending, 'true');
        if (this.isCampaignVisit) this.configureCampaignReturnTo();
      };
      this.handleCopy = () => this.copyDiscountCode();
      this.handleSectionSelect = (event) => {
        if (event.detail?.sectionId !== this.dataset.sectionId) return;
        this.requestOpen('theme-editor', true);
      };

      this.closeButton?.addEventListener('click', this.handleCloseClick);
      this.dialog.addEventListener('click', this.handleDialogClick);
      this.dialog.addEventListener('cancel', this.handleCancel);
      this.dialog.addEventListener('close', this.handleDialogClose);
      this.dialog.addEventListener('keydown', this.handleKeydown);
      this.form?.addEventListener('submit', this.handleFormSubmit);
      this.copyButton?.addEventListener('click', this.handleCopy);
      document.addEventListener('shopify:section:select', this.handleSectionSelect);

      this.configureDiscountLink();
      this.initializeState();
    }

    disconnectedCallback() {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.retryTimer);
      window.clearTimeout(this.copyResetTimer);
      document.removeEventListener('mouseout', this.handleExitIntent);
      document.removeEventListener('shopify:section:select', this.handleSectionSelect);
      this.closeButton?.removeEventListener('click', this.handleCloseClick);
      this.dialog?.removeEventListener('click', this.handleDialogClick);
      this.dialog?.removeEventListener('cancel', this.handleCancel);
      this.dialog?.removeEventListener('close', this.handleDialogClose);
      this.dialog?.removeEventListener('keydown', this.handleKeydown);
      this.form?.removeEventListener('submit', this.handleFormSubmit);
      this.copyButton?.removeEventListener('click', this.handleCopy);
    }

    initializeState() {
      const params = new URLSearchParams(window.location.search);
      const requestedState = params.get('calinfi_email_popup');
      const forcePreview = requestedState === 'preview';
      const forceSuccess = requestedState === 'subscribed' || this.serverState === 'success';
      const campaignRequested = requestedState === 'campaign';
      const campaignReturn = params.get('source') === 'meta_lead';
      const hasServerError = this.serverState === 'error';
      const hasPendingSubmission = readStorage(window.sessionStorage, STORAGE_KEYS.pending) === 'true';
      const hasVerifiedReturn =
        requestedState === 'subscribed' && hasPendingSubmission && !hasServerError;
      const inDesignMode = this.dataset.designMode === 'true';

      this.isCampaignVisit = campaignRequested || campaignReturn;
      if (this.isCampaignVisit) this.configureCampaignForm();

      if (forceSuccess) {
        writeStorage(window.localStorage, STORAGE_KEYS.subscribed, 'true');
        writeStorage(window.sessionStorage, STORAGE_KEYS.pending, 'false');
        this.showSuccessState();
        this.requestOpen('success', true);
        this.maybeFireSubscriptionLead(hasVerifiedReturn);
        this.removeStateParameter();
        return;
      }

      if (hasServerError || hasPendingSubmission) {
        writeStorage(window.sessionStorage, STORAGE_KEYS.pending, 'false');
        if (hasServerError) writeStorage(window.sessionStorage, STORAGE_KEYS.leadPending, 'false');
        this.requestOpen('form-return', true);
        return;
      }

      if (forcePreview || inDesignMode) {
        this.requestOpen(forcePreview ? 'preview' : 'theme-editor', true);
        if (forcePreview) this.removeStateParameter();
        return;
      }

      if (campaignRequested) {
        if (this.isCampaignEligible()) this.requestOpen('campaign', true);
        return;
      }

      if (!this.isEligible()) return;

      const isMobile = window.matchMedia(MOBILE_QUERY).matches;
      const delay = Number(isMobile ? this.dataset.mobileDelay : this.dataset.desktopDelay) || 0;
      this.openTimer = window.setTimeout(() => this.requestOpen('timer'), delay);

      if (!isMobile) document.addEventListener('mouseout', this.handleExitIntent);
    }

    isEligible() {
      if (this.dataset.enabled !== 'true') return false;
      if (this.dataset.pageEligible !== 'true') return false;
      if (this.dataset.customerIneligible === 'true') return false;
      if (readStorage(window.localStorage, STORAGE_KEYS.subscribed) === 'true') return false;
      if (readStorage(window.sessionStorage, STORAGE_KEYS.shown) === 'true') return false;

      const closedUntil = Number(readStorage(window.localStorage, STORAGE_KEYS.closedUntil) || 0);
      return !closedUntil || Date.now() >= closedUntil;
    }

    isCampaignEligible() {
      if (this.dataset.enabled !== 'true') return false;
      if (this.dataset.pageEligible !== 'true') return false;
      if (this.dataset.customerIneligible === 'true') return false;
      return readStorage(window.localStorage, STORAGE_KEYS.subscribed) !== 'true';
    }

    requestOpen(source, force = false) {
      if (this.dialog.open) return;
      if (!force && !this.isEligible()) return;

      if (this.isObstructed()) {
        window.clearTimeout(this.retryTimer);
        this.retryTimer = window.setTimeout(() => this.requestOpen(source, force), 600);
        return;
      }

      this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.openSource = source;

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      document.body.classList.add('calinfi-email-popup-open');
      if (!force) writeStorage(window.sessionStorage, STORAGE_KEYS.shown, 'true');
      document.removeEventListener('mouseout', this.handleExitIntent);

      window.requestAnimationFrame(() => {
        const firstTarget = this.isSuccess
          ? this.copyButton || this.closeButton
          : this.querySelector('input[type="email"]') || this.closeButton;
        firstTarget?.focus({ preventScroll: true });
      });

      this.dispatchEvent(
        new CustomEvent('calinfi:email-popup:open', {
          bubbles: true,
          detail: { source },
        })
      );
    }

    isObstructed() {
      if (document.body.classList.contains('calinfi-payment-modal-open')) return true;

      const selectors = [
        'cart-drawer.active',
        'dialog[open]',
        '[aria-modal="true"]',
        '#shopify-pc__banner',
        '.shopify-pc__banner__dialog',
        '[data-privacy-banner]',
      ];

      return Array.from(document.querySelectorAll(selectors.join(','))).some((element) => {
        if (element === this.dialog || this.contains(element)) return false;
        return isVisible(element);
      });
    }

    closePopup(reason) {
      if (!this.dialog.open) return;

      if (reason === 'dismiss' && !this.isSuccess && !['preview', 'theme-editor'].includes(this.openSource)) {
        const days = Number(this.dataset.closeSuppressionDays) || 14;
        writeStorage(window.localStorage, STORAGE_KEYS.closedUntil, String(Date.now() + days * DAY_IN_MS));
      }

      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.afterClose();
      }
    }

    afterClose() {
      document.body.classList.remove('calinfi-email-popup-open');
      if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
      this.previousFocus = null;
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    showSuccessState() {
      this.isSuccess = true;
      if (this.formPanel) this.formPanel.hidden = true;
      if (this.successPanel) this.successPanel.hidden = false;
      this.dispatchEvent(new CustomEvent('calinfi:email-popup:success', { bubbles: true }));
    }

    configureDiscountLink() {
      if (!this.discountLink) return;
      const redirectUrl = new URL(window.location.href);
      redirectUrl.searchParams.delete('calinfi_email_popup');
      redirectUrl.searchParams.set('offer', this.discountCode);
      const redirect = `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`;
      this.discountLink.href = `/discount/${encodeURIComponent(this.discountCode)}?redirect=${encodeURIComponent(redirect)}`;
    }

    configureCampaignForm() {
      if (this.tagsInput) {
        const tags = new Set(
          this.tagsInput.value
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean)
        );
        tags.add('source_meta_paid');
        tags.add('website_odun_launch_2026_07');
        tags.add('offer_welcome10');
        this.tagsInput.value = Array.from(tags).join(',');
      }

      this.configureCampaignReturnTo();
    }

    configureCampaignReturnTo() {
      if (!this.returnToInput) return;
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set('calinfi_email_popup', 'subscribed');
      returnUrl.searchParams.set('source', 'meta_lead');
      returnUrl.hash = '';
      this.returnToInput.value = `${returnUrl.pathname}${returnUrl.search}`;
    }

    maybeFireSubscriptionLead(hasVerifiedReturn = false) {
      const hasVerifiedSuccess = this.serverState === 'success' || hasVerifiedReturn;
      const hasPendingSubmission = readStorage(window.sessionStorage, STORAGE_KEYS.leadPending) === 'true';
      const alreadyFired = readStorage(window.sessionStorage, STORAGE_KEYS.leadFired) === 'true';

      if (!hasVerifiedSuccess || !hasPendingSubmission || alreadyFired) return;
      this.fireSubscriptionLead();
    }

    fireSubscriptionLead(attempt = 0, eventId = null) {
      const leadEventId =
        eventId || `welcome10-meta-lead-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const eventData = {
        eventId: leadEventId,
        contentName: 'CALINFI WELCOME10 subscription',
        contentCategory: 'Website subscription',
        currency: 'USD',
        value: 0,
      };

      if (typeof window.Shopify?.analytics?.publish === 'function') {
        try {
          const published = window.Shopify.analytics.publish('calinfi:lead', eventData);
          if (published !== false) {
            this.completeLeadDispatch(leadEventId);
            return;
          }
        } catch (_error) {}
      }

      if (typeof window.fbq === 'function') {
        window.fbq(
          'track',
          'Lead',
          {
            content_name: eventData.contentName,
            content_category: eventData.contentCategory,
            currency: eventData.currency,
            value: eventData.value,
          },
          { eventID: leadEventId }
        );
        this.completeLeadDispatch(leadEventId);
        return;
      }

      if (attempt < 40) {
        window.setTimeout(() => this.fireSubscriptionLead(attempt + 1, leadEventId), 250);
      }
    }

    completeLeadDispatch(eventId) {
      writeStorage(window.sessionStorage, STORAGE_KEYS.leadFired, 'true');
      writeStorage(window.sessionStorage, STORAGE_KEYS.leadPending, 'false');
      this.dispatchEvent(
        new CustomEvent('calinfi:email-popup:lead', {
          bubbles: true,
          detail: { eventId },
        })
      );
    }

    async copyDiscountCode() {
      if (!this.copyButton || !this.copyLabel) return;

      let copied = false;

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(this.discountCode);
          copied = true;
        }
      } catch (_error) {
        // Some storefront browsers expose the Clipboard API but block it. Fall through to the legacy copy path.
      }

      if (!copied) {
        let input;

        try {
          input = document.createElement('textarea');
          input.value = this.discountCode;
          input.setAttribute('readonly', '');
          input.style.position = 'fixed';
          input.style.opacity = '0';
          document.body.appendChild(input);
          input.select();
          copied = document.execCommand('copy');
        } catch (_error) {
          copied = false;
        } finally {
          input?.remove();
        }
      }

      if (copied) {
        this.copyLabel.textContent = this.copyButton.dataset.copiedLabel || 'Copied';
        this.copyButton.setAttribute('aria-label', this.copyButton.dataset.copiedAnnouncement || 'Discount code copied');
        window.clearTimeout(this.copyResetTimer);
        this.copyResetTimer = window.setTimeout(() => {
          this.copyLabel.textContent = this.copyButton.dataset.copyLabel || 'Copy';
          this.copyButton.setAttribute('aria-label', this.copyAriaLabel);
        }, 2200);
      } else {
        this.copyLabel.textContent = this.discountCode;
        this.copyButton.setAttribute('aria-label', `Copy unavailable. Discount code ${this.discountCode}`);
      }
    }

    removeStateParameter() {
      const url = new URL(window.location.href);
      url.searchParams.delete('calinfi_email_popup');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }

  customElements.define('calinfi-email-popup', CalinfiEmailPopup);
})();
